/* eslint-disable react/jsx-props-no-spreading */
import { Controller } from 'react-hook-form';

// Domain
import {
  getFieldTypeConfig,
  getFieldType,
  getFieldId,
  getFieldHelp,
  getFieldOptions,
  isFieldMultiple,
  getFieldData,
  getFileUploadUrl,
  isLiveSearchEnabledField,
  getFieldSuggestionsUrl,
  hasFieldSuggestions,
  getFieldLib,
} from '../../../domain/form/formUseCase';
import { encodeFieldName } from '../../../domain/form/fieldNameCodec';

// Components
import Input from '../../molecules/Input/Input';
import Textarea from '../Textarea/Textarea';
import FileField from '../../molecules/Fields/FileField/FileField';
import EnumField from '../../molecules/Fields/EnumField/EnumField';
import EnumMultiField from '../../molecules/Fields/EnumField/EnumMultiField';
import SelectClassField from '../../molecules/Fields/SelectClassField/SelectClassField';
import ColorField from '../../molecules/Fields/ColorField/ColorField';
import DateTimeField from '../../molecules/Fields/DateTimeField/DateTimeField';
import TimeField from '../../molecules/Fields/TimeField/TimeField';
import TinyMceField from '../../molecules/Fields/TinyMceField/TinyMceField';
import BooleanField from '../../molecules/Fields/BooleanField/BooleanField';
import TableField from '../../molecules/Fields/TableField/TableField';
import EntityField from '../../molecules/Fields/EntityField/EntityField';
import CheckboxListField from '../../molecules/Fields/CheckboxListField/CheckboxListField';
import RadioListField from '../../molecules/Fields/RadioListField/RadioListField';
import SuggestField from '../../molecules/Fields/SuggestField/SuggestField';
import Checkbox from '../Checkbox/Checkbox';
import Radio from '../Radio/Radio';
import GridField from '../../molecules/Fields/GridField/GridField';
import FieldWrapper from '../FormFieldWrapper/FormFieldWrapper';

/**
 * @typedef {import('react-hook-form').FieldValues} FieldValues
 * @typedef {import('react-hook-form').Control<FieldValues, any>} Control
 *
 * Field renderer component with accessibility
 * @param {object} props - Component props
 * @param {FormItem} props.field - Form item (discriminated union)
 * @param {Function} props.register - React Hook Form register function
 * @param {Control} props.control - React Hook Form control object
 * @param {FieldValues | { message?: string }} [props.error] - Field error object
 * @param {import('react-hook-form').UseFormSetValue<FieldValues>} [props.setValue] - React Hook Form setValue
 * @param {import('react-hook-form').UseFormGetValues<FieldValues>} [props.getValues] - React Hook Form getValues
 * @param {FormLayout} [props.layout] - Form layout (fieldsById)
 * @param {import('react-hook-form').FieldErrors<FieldValues>} [props.errors] - All form errors (used by grid nested fields)
 * @param {{ getCellValue: (rowId: string, colId: string) => string | undefined, isRowVisible: (row: FormTableRow) => boolean }} [props.computed]
 * @param {Set<string>} [props.readOnlyFieldIds]
 * @param {Set<string>} [props.disabledFieldIds]
 * @returns {JSX.Element | null} Rendered field
 */
function FieldRenderer({
  field, register, control, error, setValue, getValues, layout, errors = {}, computed,
  readOnlyFieldIds, disabledFieldIds,
}) {
  const fieldId = getFieldId(field);
  if (!fieldId) return null;
  const rhfName = encodeFieldName(fieldId);

  const fieldType = getFieldType(field);
  const fieldConfig = getFieldTypeConfig(fieldType);
  const fieldData = getFieldData(field);
  const fieldHelp = getFieldHelp(field);
  const fieldOptions = getFieldOptions(field);
  const isMultiple = isFieldMultiple(field);

  // Get align from field data (for text fields)
  // TypeScript needs explicit type assertion for discriminated union properties
  const align = (fieldData && 'align' in fieldData) ? fieldData.align : 'left';
  const fieldIdAttr = `field-${rhfName}`;
  const errorId = error ? `${fieldIdAttr}-error` : undefined;
  const helpId = fieldHelp ? `${fieldIdAttr}-help` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
  const isLiveSearch = isLiveSearchEnabledField(field);
  const suggestionsUrl = getFieldSuggestionsUrl(field);
  const withSuggestions = hasFieldSuggestions(field);

  const isDisabled = Boolean(disabledFieldIds?.has(rhfName));
  const isReadOnly = Boolean(readOnlyFieldIds?.has(rhfName));

  const commonProps = {
    id: fieldIdAttr,
    name: rhfName,
    disabled: isDisabled,
    readOnly: isReadOnly,
    isReadOnly,
    'aria-describedby': describedBy,
    'aria-invalid': error ? 'true' : 'false',
    placeholder: '',
    style: /** @type {import('react').CSSProperties} */ ({ textAlign: align }),
  };

  /**
   * @param {'text' | 'email' | 'number'} inputType
   * @returns {JSX.Element}
   */
  const renderTextInput = (inputType) => {
    if (withSuggestions && suggestionsUrl) {
      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <SuggestField
              {...commonProps}
              {...fieldProps}
              type={inputType}
              suggestionsUrl={suggestionsUrl}
              hasError={Boolean(error)}
              isLiveSearch={isLiveSearch}
            />
          )}
        />
      );
    }

    return (
      <Input
        {...commonProps}
        {...register(rhfName)}
        type={inputType === 'text' ? undefined : inputType}
        hasError={Boolean(error)}
        isLiveSearch={isLiveSearch}
      />
    );
  };

  // Render based on field type
  switch (fieldConfig.component) {
    case 'text':
      return renderTextInput('text');

    case 'email':
      return renderTextInput('email');

    case 'int':
      return renderTextInput('number');

    case 'textarea':
      return (
        <Textarea
          {...commonProps}
          {...register(rhfName)}
          rows={4}
          theme="default"
          hasError={Boolean(error)}
        />
      );

    // TODO FORM: Ensure nesting all enum types in this case is a good idea
    // Le case select regroupe les diferents stypes de select (enum, enumMultiple, selectClass, selectMultipleClass)
    case 'select': {
      // condition utilisée pour savoir si on est sur un selectSclass + conditions pour aider le typage et éviter les erreurs typescript
      if (field.selectClass && 'selectOptionsUrl' in field.selectClass && typeof field.selectClass.selectOptionsUrl === 'string') {
        // Utiliser SelectClassField pour charger les options via URL
        const selectOptionsUrl = field.selectClass.selectOptionsUrl || '';
        return (
          <Controller
            name={rhfName}
            control={control}
            render={({ field: { ref, ...fieldProps } }) => (
              <SelectClassField
                selectOptionsUrl={selectOptionsUrl}
                theme="default"
                {...commonProps}
                {...fieldProps}
                error={typeof error === 'string' ? error : error?.message}
              />
            )}
          />
        );
      }

      // Comportement existant pour select/selectMultiple avec options statiques
      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            isMultiple ? (
              <EnumMultiField
              // @ts-ignore
                directEdit={{}}
                options={fieldOptions}
                {...commonProps}
                {...fieldProps}
                error={typeof error === 'string' ? error : error?.message}
              />
            ) : (
              <EnumField
                theme="default"
                // @ts-ignore
                directEdit={{}}
                options={fieldOptions}
                {...commonProps}
                {...fieldProps}
                error={typeof error === 'string' ? error : error?.message}
              />
            )
          )}
        />
      );
    }

    case 'file':
      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <FileField
              {...commonProps}
              {...fieldProps}
              uploadUrl={getFileUploadUrl(field)}
            />
          )}
        />
      );

    case 'color':
      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <ColorField
              theme="default"
              {...commonProps}
              {...fieldProps}
            />
          )}
        />
      );

    case 'date':
      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <DateTimeField
              type="datetime-local"
              {...commonProps}
              {...fieldProps}
            />
          )}
        />
      );

    case 'time':
      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <TimeField
              {...commonProps}
              {...fieldProps}
            />
          )}
        />
      );

    case 'wysiwyg':
      return (
        <Controller
          name={rhfName}
          control={control}
          render={({
            field: {
              value, onChange,
            },
          }) => (
            <TinyMceField
              {...commonProps}
              value={value}
              onChange={onChange}
              error={error?.message}
            />
          )}
        />
      );

    case 'boolean':
      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => {
            // Ensure value is 'oui' or 'non', default to 'oui' if invalid
            const booleanValue = (fieldProps.value === 'oui' || fieldProps.value === 'non')
              ? fieldProps.value
              : 'oui';
            return (
              <BooleanField
                name={rhfName}
                value={booleanValue}
                onChange={fieldProps.onChange}
                disabled={isDisabled}
                isReadOnly={isReadOnly}
                error={error}
                className=""
              />
            );
          }}
        />
      );

    case 'mergeTable': {
      const tableData = field.mergeTable?.table;
      if (!tableData) return null;

      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <TableField
              tableData={tableData}
              isReadOnly={isReadOnly}
              {...fieldProps}
            />
          )}
        />
      );
    }

    case 'entity': {
      const entityData = field.entity;
      if (!entityData) return null;

      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <EntityField
              {...commonProps}
              {...fieldProps}
              baseFormUrl={entityData.baseFormUrl}
              multiple={entityData.multiple}
              error={typeof error === 'string' ? error : error?.message}
              setValue={setValue}
              getValues={getValues}
              layout={layout}
            />
          )}
        />
      );
    }

    case 'checkboxList': {
      const checkboxListData = field.checkboxList;
      if (!checkboxListData) return null;

      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <CheckboxListField
              {...commonProps}
              {...fieldProps}
              headers={checkboxListData.headers}
              groups={checkboxListData.groups}
              ariaDescribedBy={describedBy}
              error={typeof error === 'string' ? error : error?.message}
            />
          )}
        />
      );
    }

    case 'radioList': {
      const radioListData = field.radioList;
      if (!radioListData) return null;

      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, ...fieldProps } }) => (
            <RadioListField
              {...commonProps}
              {...fieldProps}
              headers={radioListData.headers}
              groups={radioListData.groups}
              ariaDescribedBy={describedBy}
              error={typeof error === 'string' ? error : error?.message}
            />
          )}
        />
      );
    }

    case 'checkbox': {
      if (!field.checkbox) return null;

      return (
        <Controller
          name={rhfName}
          control={control}
          render={({ field: { ref, value, onChange } }) => (
            <Checkbox
              id={fieldIdAttr}
              ref={ref}
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              label={getFieldLib(field)}
              size="small"
              theme="black"
              disabled={isDisabled}
              isReadOnly={isReadOnly}
              aria-describedby={describedBy}
            />
          )}
        />
      );
    }

    case 'radio': {
      if (!field.radio) return null;
      const optionValue = field.radio.value ?? '';
      const radioRegistration = register(rhfName);

      return (
        <Radio
          // Options of one group share the RHF name, so the DOM id must carry the value
          id={`${fieldIdAttr}-${optionValue}`}
          {...radioRegistration}
          onChange={isReadOnly ? () => {} : radioRegistration.onChange}
          value={optionValue}
          label={getFieldLib(field)}
          size="small"
          theme="black"
          disabled={isDisabled}
          isReadOnly={isReadOnly}
          aria-describedby={describedBy}
          aria-invalid={error ? 'true' : 'false'}
        />
      );
    }

    case 'grid': {
      const tableData = field.grid?.table;
      if (!tableData) return null;

      return (
        <GridField
          tableData={tableData}
          computed={computed}
          renderNestedField={(nestedField) => {
            const nestedId = getFieldId(nestedField);
            if (!nestedId) return null;
            const nestedError = errors[encodeFieldName(nestedId)];
            return (
              <FieldWrapper field={nestedField} error={nestedError}>
                <FieldRenderer
                  field={nestedField}
                  register={register}
                  control={control}
                  error={nestedError}
                  setValue={setValue}
                  getValues={getValues}
                  layout={layout}
                  errors={errors}
                  computed={computed}
                  readOnlyFieldIds={readOnlyFieldIds}
                  disabledFieldIds={disabledFieldIds}
                />
              </FieldWrapper>
            );
          }}
        />
      );
    }

    default:
      return (
        <span>
          {`Add support for this field type ${fieldType}`}
        </span>
      );
  }
}

export default FieldRenderer;
